import type Phaser from "phaser";
import { FONTS } from "./theme";

const SCALE = 2;

export function addWoodText(
  scene: Phaser.Scene,
  x: number,
  y: number,
  text: string,
  fontSize = 22,
): Phaser.GameObjects.Image {
  const key = `wood-text:${fontSize}:${text}`;
  ensureWoodTextTexture(scene, key, text, fontSize);
  const image = scene.add.image(x, y, key).setOrigin(0.5);
  const source = scene.textures.get(key).getSourceImage() as HTMLCanvasElement;
  image.setDisplaySize(source.width / SCALE, source.height / SCALE);
  return image;
}

function ensureWoodTextTexture(
  scene: Phaser.Scene,
  key: string,
  text: string,
  fontSize: number,
): void {
  if (scene.textures.exists(key)) {
    return;
  }

  const font = `400 ${fontSize}px ${FONTS.wood}`;
  const measure = document.createElement("canvas").getContext("2d");
  if (!measure) {
    return;
  }
  measure.font = font;
  const pad = Math.ceil(fontSize * 0.48);
  const width = Math.max(2, Math.ceil(measure.measureText(text).width) + pad * 2);
  const height = Math.ceil(fontSize * 1.55) + pad;

  const texture = scene.textures.createCanvas(key, width * SCALE, height * SCALE);
  const ctx = texture?.getContext();
  if (!texture || !ctx) {
    return;
  }

  ctx.scale(SCALE, SCALE);
  ctx.font = font;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  ctx.miterLimit = 2;

  const cx = width / 2;
  const cy = height / 2 + fontSize * 0.05;

  ctx.strokeStyle = "rgba(18, 10, 4, 0.42)";
  ctx.lineWidth = fontSize * 0.36;
  ctx.strokeText(text, cx, cy + fontSize * 0.07);

  ctx.strokeStyle = "#2a1a10";
  ctx.lineWidth = fontSize * 0.3;
  ctx.strokeText(text, cx, cy);

  const bevel = ctx.createLinearGradient(cx, cy - fontSize * 0.55, cx, cy + fontSize * 0.5);
  bevel.addColorStop(0, "#f3e6c8");
  bevel.addColorStop(0.42, "#e4c48a");
  bevel.addColorStop(1, "#a67a3a");
  ctx.strokeStyle = bevel;
  ctx.lineWidth = fontSize * 0.18;
  ctx.strokeText(text, cx, cy);

  const pattern = ctx.createPattern(woodGrainTile(fontSize), "repeat");
  ctx.fillStyle = pattern ?? "#4a2c16";
  ctx.fillText(text, cx, cy);

  texture.refresh();
}

function woodGrainTile(fontSize: number): HTMLCanvasElement {
  const tile = document.createElement("canvas");
  tile.width = Math.max(6, Math.round(fontSize / 5));
  tile.height = Math.max(18, fontSize);
  const ctx = tile.getContext("2d");
  if (!ctx) {
    return tile;
  }
  const grain = ctx.createLinearGradient(0, 0, tile.width, 0);
  grain.addColorStop(0, "#3a2010");
  grain.addColorStop(0.35, "#5c3a1e");
  grain.addColorStop(0.55, "#2e180c");
  grain.addColorStop(1, "#4a2c16");
  ctx.fillStyle = grain;
  ctx.fillRect(0, 0, tile.width, tile.height);
  ctx.strokeStyle = "rgba(18, 8, 4, 0.32)";
  ctx.lineWidth = 1;
  for (let x = 0.5; x < tile.width; x += 1.5) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x + 0.6, tile.height);
    ctx.stroke();
  }
  return tile;
}
